/**
 * SHA-256 Hash Utilities for Audit Trail
 *
 * Provides cryptographic hash functions for audit trail immutability.
 * Uses Web Crypto API (available in all modern browsers).
 *
 * @module utils/hashUtils
 */

/**
 * Generate SHA-256 hash of a string
 * @param {string} message - Message to hash
 * @returns {Promise<string>} Hex-encoded hash
 */
export async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Generate hash for audit entry
 * Hash includes: id + timestamp + userId + itemId + action + previousHash
 *
 * @param {Object} entry - Audit entry object
 * @returns {Promise<string>} SHA-256 hash
 */
export async function generateAuditHash(entry) {
  const hashInput = [
    entry.id || '',
    entry.timestamp || '',
    entry.user?.id || '',
    entry.item?.id || '',
    entry.action || '',
    entry.previousHash || 'genesis' // First entry uses 'genesis'
  ].join('|');

  return await sha256(hashInput);
}

/**
 * Verify hash chain integrity
 * Recalculates all hashes and checks they match stored hashes
 *
 * @param {Array} entries - Array of audit entries (oldest first)
 * @returns {Promise<Object>} { valid: boolean, brokenAt: number|null }
 */
export async function verifyHashChain(entries) {
  if (!entries || entries.length === 0) {
    return { valid: true, brokenAt: null };
  }

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];

    // Skip entries without hash (legacy entries from before Days 16-17)
    if (!entry.hash) {
      continue;
    }

    // Calculate expected hash
    const expectedHash = await generateAuditHash(entry);

    // Check if stored hash matches
    if (entry.hash !== expectedHash) {
      return {
        valid: false,
        brokenAt: i,
        entry: entry,
        expectedHash,
        actualHash: entry.hash
      };
    }

    // Check if previousHash points to correct entry
    if (i > 0) {
      const previousEntry = entries[i - 1];

      // Skip if previous entry has no hash (legacy)
      if (previousEntry.hash && entry.previousHash !== previousEntry.hash) {
        return {
          valid: false,
          brokenAt: i,
          reason: 'Previous hash mismatch',
          entry: entry
        };
      }
    }
  }

  return { valid: true, brokenAt: null };
}

/**
 * Sort entries by timestamp for correct chain ordering
 * Important: Multi-device sync may receive entries out of order
 *
 * @param {Array} entries - Array of audit entries
 * @returns {Array} Sorted entries (oldest first)
 */
export function sortEntriesByTimestamp(entries) {
  return [...entries].sort((a, b) =>
    new Date(a.timestamp) - new Date(b.timestamp)
  );
}
